import { useEffect } from 'react';

import { useGateValue, useStatsigClient } from '@coursera-web/statsig';

import { ORG_SCOPED_HOME_STATSIG_GATE } from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useOrgScopedHomeFeature } from 'bundles/unified-common/contexts/OrgScopedHomeFeatureContext';

/**
 * Determines if the org-scoped unified home experience is enabled.
 *
 * Requires BOTH:
 * 1. The Statsig gate `org-scoped-home` is passing (user-level rollout)
 * 2. The `enableOrgScopedHome` product feature is enabled for the org
 *
 * The Statsig exposure is only logged when both conditions are confirmed true,
 * preventing impression pollution for users whose org is not yet in the phased rollout.
 *
 * Reads the product feature from EnterpriseProductFeaturesContext (available on enterprise/org-scoped pages)
 * or from OrgScopedHomeFeatureContext (available inside UnifiedUserExperienceHandler on consumer pages).
 */
export function useIsOrgScopedHome(): boolean {
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const { checkGate } = useStatsigClient();
  const features = useEnterpriseProductFeatures();
  const orgScopedHomeFeature = useOrgScopedHomeFeature();

  const featureEnabled = features !== undefined ? features.enableOrgScopedHome ?? false : orgScopedHomeFeature.enabled;

  const isEnabled = userInOrgScopedRollout && featureEnabled;

  // Log the Statsig exposure only when both the gate and product feature pass.
  // This prevents polluting analytics with impressions for users whose org
  // is not yet enabled in the phased rollout.
  // checkGate is stable (singleton client reference from StatsigProvider).
  useEffect(() => {
    if (isEnabled) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [isEnabled, checkGate]);

  return isEnabled;
}
